import type { LocaleCode, ShiftLedgerSummary } from '@/api/models';
import { useShiftLedgerSummaryQuery } from '@/api/queries';

type UseShiftRollupDrawerOptions = {
    /** The shift to summarize; `null` closes the drawer and fetches nothing. */
    shiftId: string | null;
    locale: LocaleCode;
};

export type UseShiftRollupDrawerResult = {
    summary: ShiftLedgerSummary | null;
    isLoading: boolean;
    /** A failed read. Rendered inside the drawer, never thrown. */
    hasError: boolean;
};

/**
 * Drives the shift rollup drawer.
 *
 * Self-served: it owns the read for the shift it was handed rather than being
 * passed a row the register already had. That is what lets it show the **ledger
 * lines**, which the register does not carry, and what keeps the two reads
 * independent — the register is a list of shifts, the rollup is a reading of the
 * journal, and one being unavailable says nothing about the other.
 *
 * **A failed read is rendered here, not thrown.** Every page in this area throws
 * to its boundary; a drawer is the one place that would be wrong, because
 * throwing unmounts the working register behind it over one shift that would not
 * open.
 */
export const useShiftRollupDrawer = ({
    shiftId,
    locale,
}: UseShiftRollupDrawerOptions): UseShiftRollupDrawerResult => {
    const query = useShiftLedgerSummaryQuery(shiftId, locale, true);
    const summary = query.data?.success ? query.data.response : null;
    const hasError = query.data ? !query.data.success : Boolean(query.error);

    return {
        summary,
        isLoading: shiftId !== null && query.isLoading,
        hasError,
    };
};
